import { useAuthStore } from '@/stores/useAuthStore';
import { useCardStore } from '@/stores/useCardStore';
import i18n from '@/translations/config';
import { Query } from '@/types/query';
import { handleError } from '@/utils/handleError';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

export const useCards = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<string>(
    searchParams.get('filter') || 'my',
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { listCards, cards, pagination, createCard } = useCardStore();
  const { user, isAuth, loading: authLoading } = useAuthStore();
  const { t } = useTranslation();

  const getQueryParams = useCallback(
    (page: number): Query => {
      const params = {
        search: searchValue,
        current_page: page,
        per_page: pagination.per_page || 10,
        sort_order: sortOrder,
        filter,
      };

      switch (filter) {
        case 'my':
          return { ...params, user_id: user?.id };
        case 'company':
          return { ...params, company_id: user?.company?.id };
        default:
          return params;
      }
    },
    [
      searchValue,
      sortOrder,
      pagination.per_page,
      user?.id,
      user?.company?.id,
      filter,
    ],
  );

  useEffect(() => {
    const loadCards = async () => {
      if (!isAuth || authLoading || !user) return;
      await listCards(getQueryParams(1));
    };

    loadCards();
  }, [
    filter,
    searchValue,
    sortOrder,
    listCards,
    isAuth,
    authLoading,
    user,
    getQueryParams,
  ]);

  const showFilterSelect = useMemo(
    () => user?.role === 'manager' || user?.role === 'admin',
    [user?.role],
  );

  const filterOptions = useMemo(() => {
    const options = [
      { label: t('cardsList:allCards'), value: 'all' },
      { label: t('cardsList:myCards'), value: 'my' },
    ];

    if (showFilterSelect && user?.company?.id) {
      options.push({
        label: t('cardsList:companyCards'),
        value: 'company',
      });
    }

    return options;
  }, [t, showFilterSelect, user?.company?.id]);

  const handleCreateCard = async (userId: number) => {
    try {
      const newCard = await createCard(userId);
      if (newCard) {
        navigate(`/${i18n.language}/dashboard/cards/${newCard.slug}`);
      }
    } catch (error) {
      handleError(error, undefined, navigate);
    }
  };

  const handleModify = (slug: string) => {
    navigate(`/${i18n.language}/dashboard/cards/${slug}`);
  };

  const handleFilterChange = (e: {
    target: {
      name: string;
      value: string | number | boolean | (string | number | boolean)[];
    };
  }) => {
    const newFilter = e.target.value as string;
    setFilter(newFilter);
    setSearchParams(prev => {
      prev.set('filter', newFilter);
      return prev;
    });
  };

  const handleAddCard = () => {
    if (user?.role === 'user') {
      handleCreateCard(user.id);
    } else {
      setIsCreateModalOpen(true);
    }
  };

  const fetchMoreData = useCallback(() => {
    listCards(getQueryParams(pagination.current_page + 1));
  }, [listCards, getQueryParams, pagination.current_page]);

  return {
    searchValue,
    setSearchValue,
    sortOrder,
    setSortOrder,
    filter,
    cards,
    pagination,
    filterOptions,
    isCreateModalOpen,
    setIsCreateModalOpen,
    handleCreateCard,
    handleModify,
    handleFilterChange,
    handleAddCard,
    fetchMoreData,
    showFilterSelect,
  };
};
